/**
 * One encounter's event stream, off the admin recent-activity timeline
 * (GDN-2774), plus the two follow-on reads the events pane needs: a single
 * event's payload, and whether a catalog tag exists here at all (GDN-2776).
 *
 * The billing worklist page asks the stream question twice per row — once for
 * the coverage badge in the table, once again when the row is expanded — so
 * both callers share a single hook with identical query args. RTK Query dedupes
 * on those args, so a row costs one request no matter how many times it's read,
 * and expanding a row is free: the entries are already cached. `tag: undefined`
 * serialises away entirely, so an unfiltered read from either caller still
 * lands on the same cache entry.
 *
 * The probe is deliberately capped rather than paginated. This page measures
 * whether events exist at all, not how many; `hasMore` is enough to say "at
 * least N" honestly without a second round trip.
 */
import { useGetRecentActivityQuery } from '../store/hooks';
import type { RecentActivityEntry } from '../store/api';
import { isUnknownActivityTag } from '../utils/activity-tag';
import { isPhiAccessDenied } from '../utils/phi-access';

/** The envelope-flag lens, matching the recent-activity `category` param. */
export type EventLens = 'all' | 'domain' | 'audit' | 'usage';

/** How many events a single row's probe pulls. See the note above. */
export const EVENT_PROBE_LIMIT = 25;

export interface EncounterEvents {
  entries: RecentActivityEntry[];
  /** Events found, capped at EVENT_PROBE_LIMIT — see `hasMore`. */
  count: number;
  /** True when the encounter has more events than the probe pulled. */
  hasMore: boolean;
  isLoading: boolean;
  /** The load finished and found nothing. The gap this page exists to expose. */
  isEmpty: boolean;
  /** The PHI gate refused this caller. */
  isDenied: boolean;
  /** The requested tag is not in this environment's event catalog. */
  isUnknownTag: boolean;
  /** The timeline failed for any other reason (store down, query error). */
  isFailed: boolean;
}

export const useEncounterEvents = ({
  organizationId,
  encounterId,
  lens,
  tag,
  skip = false,
}: {
  organizationId: string;
  encounterId: string;
  lens: EventLens;
  /** Catalog tag to narrow to. Omit for the unfiltered timeline. */
  tag?: string;
  skip?: boolean;
}): EncounterEvents => {
  const { data, isLoading, isFetching, error } = useGetRecentActivityQuery(
    {
      organizationId,
      encounterId,
      category: lens,
      tag,
      limit: EVENT_PROBE_LIMIT,
    },
    { skip: skip || !organizationId || !encounterId },
  );

  const entries = data?.entries ?? [];
  const loading = (isLoading || isFetching) && !data;
  const isDenied = isPhiAccessDenied(error);
  const isUnknownTag = isUnknownActivityTag(error);

  return {
    entries,
    count: entries.length,
    hasMore: !!data?.has_more,
    isLoading: loading,
    // Only a completed, successful, non-empty-by-answer load is a real gap —
    // a failure that happens to return no rows must never read as "no events".
    isEmpty: !loading && !error && entries.length === 0,
    isDenied,
    isUnknownTag,
    // Both refusals above are answers, not faults, so neither counts as failed.
    isFailed: !!error && !isDenied && !isUnknownTag,
  };
};

export interface EventPayload {
  /** The event's payload. `null` when the event carries none. */
  data: Record<string, unknown> | null;
  /** The store answered, but no such event — aged out, or wrong org. */
  isMissing: boolean;
  isLoading: boolean;
  isDenied: boolean;
  isFailed: boolean;
}

/**
 * One event's payload, fetched lazily when its row is expanded.
 *
 * List responses omit `data` by design — reading the JSON column across a busy
 * timeline is the expensive part of the query — so the payload is addressed
 * separately through the SAME endpoint with `event_id`, which returns exactly
 * that event WITH its payload. No new route, no new auth surface.
 *
 * Deliberately no `category`: the endpoint defaults it to `all` server-side, so
 * an event found under one lens can never have its own payload hidden by that
 * lens on the way back. And no `tag`: an event addressed by id is already
 * unique, and re-applying the filter could only turn a known event into a
 * confusing miss.
 */
export const useEventPayload = ({
  organizationId,
  eventId,
  skip = false,
}: {
  organizationId: string;
  eventId: string;
  skip?: boolean;
}): EventPayload => {
  const { data, isLoading, isFetching, error } = useGetRecentActivityQuery(
    { organizationId, eventId, limit: 1 },
    { skip: skip || !organizationId || !eventId },
  );

  const entry = data?.entries?.[0];
  const loading = (isLoading || isFetching) && !data;

  return {
    data: entry?.data ?? null,
    isMissing: !loading && !error && !entry,
    isLoading: loading,
    isDenied: isPhiAccessDenied(error),
    isFailed: !!error && !isPhiAccessDenied(error),
  };
};

export interface ActivityTagAvailability {
  /** The check is still in flight; treat the tag as unusable until it answers. */
  isChecking: boolean;
  /** This environment's catalog has no such tag (400 `unknown_activity_tag`). */
  isUncurated: boolean;
  /**
   * The check itself failed, so whether the tag is usable is unknown.
   *
   * Kept apart from `isUncurated` because the two are different facts, but the
   * caller should withhold the filter for either: a probe that could not answer
   * is no licence to fan the same failure out across every row.
   */
  isFailed: boolean;
}

/**
 * Whether a catalog tag can be filtered on in this environment.
 *
 * Asked once per page, org-scoped, rather than once per row: whether a tag
 * exists is a property of the environment's catalog, not of any encounter, so
 * one probe answers for every row. That matters because the alternative — let
 * each row discover the 400 for itself — turns an uncurated dev catalog into a
 * screenful of failed requests, and makes the page look broken when the honest
 * answer is "that filter isn't available here".
 */
export const useActivityTagAvailability = ({
  organizationId,
  tag,
}: {
  organizationId: string;
  tag?: string;
}): ActivityTagAvailability => {
  const { data, isLoading, isFetching, error } = useGetRecentActivityQuery(
    { organizationId, tag, limit: 1 },
    { skip: !organizationId || !tag },
  );

  return {
    isChecking: (isLoading || isFetching) && !data && !error,
    isUncurated: isUnknownActivityTag(error),
    // The 400 above is an answer ("no such tag"); anything else is the probe
    // failing to answer at all.
    isFailed: !!error && !isUnknownActivityTag(error),
  };
};
